/**
 * Programmatic SEO config — generates 30+ profession-specific landing pages
 * at /invoice-format/:profession. Each ranks for 'invoice format for X' searches.
 *
 * Schema:
 *   slug, profession (display), heroLine (1-line headline tail),
 *   intro (2-3 sentences), specifics[] (3-5 unique points per profession),
 *   sampleItems[] (3 line items typical of the trade)
 *
 * Common bits (features, steps, FAQs) are added by the template at render time.
 */

export const PROFESSIONS = [
  {
    slug: "plumbers",
    profession: "Plumbers",
    heroLine: "Bill site visits, parts, and labour with one tap.",
    intro: "Plumbers move between sites all day. The right invoicing format records labour hours, parts replaced, advance taken, and balance due — without paperwork piling up at home.",
    specifics: [
      "Itemise labour separately from parts (different GST treatment).",
      "Add visit charge as a flat line if applicable.",
      "Mention warranty period for parts in notes (3 / 6 / 12 months).",
      "Track advance vs balance per site visit.",
    ],
    sampleItems: [
      { name: "Plumbing Service Visit Charge", hsn: "998719", qty: 1, rate: 500, gst: 18 },
      { name: "Pipe Replacement (PVC, 2m)", hsn: "3917", qty: 2, rate: 320, gst: 18 },
      { name: "Mixer Tap (Standard)", hsn: "8481", qty: 1, rate: 1850, gst: 18 },
    ],
    relatedSeo: ["bill-format-small-business", "free-invoice-generator"],
  },
  {
    slug: "electricians",
    profession: "Electricians",
    heroLine: "Track wiring jobs, parts and labour cleanly.",
    intro: "Electricians juggle homes, offices and renovation sites. A clean invoice format with parts + labour split makes warranties easy and disputes rare.",
    specifics: [
      "Separate copper wiring (with HSN) from labour SAC code.",
      "Note circuit details and breaker ratings in description.",
      "Add a warranty / re-visit clause for major rewiring.",
      "Charge call-out fee for visits under 30 minutes.",
    ],
    sampleItems: [
      { name: "Site Visit + Diagnosis", hsn: "998719", qty: 1, rate: 600, gst: 18 },
      { name: "Copper Wiring 2.5 sq mm (per meter)", hsn: "8544", qty: 30, rate: 45, gst: 18 },
      { name: "MCB 32A Single Pole", hsn: "8536", qty: 2, rate: 320, gst: 18 },
    ],
    relatedSeo: ["bill-format-small-business", "free-invoice-generator"],
  },
  {
    slug: "consultants",
    profession: "Consultants",
    heroLine: "Bill retainers, hourly time and project milestones.",
    intro: "Consultants — strategy, operations, HR, finance — bill in three modes: hourly, monthly retainer, or project milestone. The right invoice format handles all three.",
    specifics: [
      "Retainer? Use the recurring invoice feature (auto-runs on the 1st).",
      "Hourly? Itemise sessions with date, hours and rate.",
      "Milestone? Quote first, then bill 30/40/30 against the quote.",
      "Always show TDS (Section 194J — 10%) so net payable is clear.",
    ],
    sampleItems: [
      { name: "Strategy Workshop (4 hours)", hsn: "998311", qty: 4, rate: 5000, gst: 18 },
      { name: "Follow-up Call + Notes (1 hour)", hsn: "998311", qty: 1, rate: 4500, gst: 18 },
      { name: "Document Review", hsn: "998311", qty: 1, rate: 3000, gst: 18 },
    ],
    relatedSeo: ["freelancer-invoice-tool", "quotation-maker"],
  },
  {
    slug: "interior-designers",
    profession: "Interior Designers",
    heroLine: "Bill design fees, supervision and procurement clearly.",
    intro: "Interior projects span months — design phase, procurement and execution. A clean invoice format separates design fees (services) from material procurement (goods, often pass-through).",
    specifics: [
      "Quote first, then milestone-bill — design 30%, procurement 50%, handover 20%.",
      "Pass-through materials? Show vendor invoice + your margin transparently.",
      "Add 'site visits' as a separate line if billed beyond a cap.",
      "Mention revision policy in terms (e.g. 2 free revisions per phase).",
    ],
    sampleItems: [
      { name: "Concept Design Phase", hsn: "998391", qty: 1, rate: 75000, gst: 18 },
      { name: "Site Supervision (4 weeks)", hsn: "998391", qty: 4, rate: 8000, gst: 18 },
      { name: "Procurement Markup (10%)", hsn: "998391", qty: 1, rate: 25000, gst: 18 },
    ],
    relatedSeo: ["quotation-maker", "freelancer-invoice-tool"],
  },
  {
    slug: "photographers",
    profession: "Photographers",
    heroLine: "Wedding, events, products — invoice every shoot type.",
    intro: "Photography is project-based. The invoice format must handle shoot fees, post-production hours, deliverables (raw + edited), and equipment rental if any.",
    specifics: [
      "Itemise shoot day separately from post-production.",
      "List deliverables clearly (RAW count, edited count, album, prints).",
      "Charge advance — 30% on booking, 70% on delivery is standard.",
      "Add usage / licensing terms in notes (personal vs commercial).",
    ],
    sampleItems: [
      { name: "Wedding Day Coverage (12 hrs)", hsn: "998387", qty: 1, rate: 65000, gst: 18 },
      { name: "Post-production (200 edited photos)", hsn: "998387", qty: 1, rate: 18000, gst: 18 },
      { name: "Photo Album (50 pages)", hsn: "4901", qty: 1, rate: 8500, gst: 18 },
    ],
    relatedSeo: ["freelancer-invoice-tool", "quotation-maker"],
  },
  {
    slug: "tuition-classes",
    profession: "Tuition / Coaching Classes",
    heroLine: "Bill monthly fees, class packages and exam prep.",
    intro: "Tuition centres bill in monthly cycles. The right format handles per-student fees, package discounts, sibling concessions, and bulk slot booking.",
    specifics: [
      "Use the recurring invoice feature for monthly fees.",
      "Apply line-item discount for sibling / referral concessions.",
      "Show fees per subject with applicable GST (private tuition is 18%).",
      "Add school / board reference for parent records.",
    ],
    sampleItems: [
      { name: "Class XII Maths (April fee)", hsn: "999293", qty: 1, rate: 4500, gst: 18 },
      { name: "Class XII Physics (April fee)", hsn: "999293", qty: 1, rate: 4500, gst: 18 },
      { name: "Sibling Discount", hsn: "—", qty: 1, rate: -500, gst: 0 },
    ],
    relatedSeo: ["bill-format-small-business", "free-invoice-generator"],
  },
  {
    slug: "doctors",
    profession: "Doctors / Clinics",
    heroLine: "Bill consultations, procedures and packages.",
    intro: "Healthcare services to outpatients are GST-exempt — but doctors need professional invoices for cash visits, packages, and any non-medical service (cosmetic, wellness).",
    specifics: [
      "Consultation fees are GST-exempt — issue Bill of Supply.",
      "Wellness / cosmetic procedures attract 18% GST — issue Tax Invoice.",
      "Use serial numbering separate for clinic and any partner pharmacy.",
      "Maintain patient anonymity — billing name only, no diagnosis.",
    ],
    sampleItems: [
      { name: "Consultation — General Physician", hsn: "999312", qty: 1, rate: 800, gst: 0 },
      { name: "Wellness Package (3 visits)", hsn: "999722", qty: 1, rate: 4500, gst: 18 },
      { name: "Diagnostic Test Referral", hsn: "999316", qty: 1, rate: 1200, gst: 0 },
    ],
    relatedSeo: ["bill-format-small-business", "non-gst-bill-generator"],
  },
  {
    slug: "builders",
    profession: "Builders / Contractors",
    heroLine: "Bill construction milestones, materials and labour.",
    intro: "Construction work is milestone-billed and GST-complex (5% / 12% / 18% slabs depending on project type). The right format avoids ITC denials and audit issues.",
    specifics: [
      "Use 5% GST (no ITC) for affordable housing under PMAY.",
      "Use 12% GST (with ITC) for residential other than affordable.",
      "Use 18% GST (with ITC) for commercial works contract.",
      "Quote first, milestone bill (mobilization, slab, finishing, handover).",
    ],
    sampleItems: [
      { name: "Mobilisation Advance (10%)", hsn: "9954", qty: 1, rate: 800000, gst: 12 },
      { name: "Plinth & Foundation Milestone", hsn: "9954", qty: 1, rate: 1200000, gst: 12 },
      { name: "Brickwork & Plastering", hsn: "9954", qty: 1, rate: 950000, gst: 12 },
    ],
    relatedSeo: ["invoice-software-traders", "quotation-maker"],
  },
  {
    slug: "wholesalers",
    profession: "Wholesalers / Distributors",
    heroLine: "Bulk orders, party-wise credit and GSTR-1 in one click.",
    intro: "Wholesalers move 50–500 invoices/month. The right format integrates with party ledger, credit cycles and bulk GSTR-1 export so monthly compliance takes 30 minutes — not 3 days.",
    specifics: [
      "Bulk import customers + products from Excel.",
      "Set credit terms per party (Net 30 / 45 / 60).",
      "Auto-track ageing in 0–30 / 31–60 / 61–90 / 90+ buckets.",
      "Tally XML import/export for accountant handover.",
    ],
    sampleItems: [
      { name: "Cement OPC 53 Grade (200 bags)", hsn: "2523", qty: 200, rate: 380, gst: 28 },
      { name: "TMT Steel 12mm (2 tonne)", hsn: "7214", qty: 2000, rate: 58, gst: 18 },
      { name: "White Cement (50 bags)", hsn: "2523", qty: 50, rate: 820, gst: 28 },
    ],
    relatedSeo: ["invoice-software-traders", "quotation-maker"],
  },
  {
    slug: "restaurants",
    profession: "Restaurants / Cafes",
    heroLine: "Daily orders, table-wise billing, UPI payments.",
    intro: "Restaurants run on speed and small margins. Fast counter billing with thermal printing and dynamic UPI per bill keeps service smooth and accounting clean — works on POS, tablet, and phone.",
    specifics: [
      "Use 5% GST (no ITC) for non-AC restaurants; 18% for 5-star.",
      "Bill table-wise — print on 58mm/80mm thermal printer.",
      "Show dynamic UPI QR per bill — customer scans + pays.",
      "Daily Z-report: total sales, GST collected, cash vs UPI vs card.",
    ],
    sampleItems: [
      { name: "Veg Thali", hsn: "996331", qty: 2, rate: 220, gst: 5 },
      { name: "Cold Coffee", hsn: "996331", qty: 2, rate: 110, gst: 5 },
      { name: "Service Charge (5%)", hsn: "—", qty: 1, rate: 33, gst: 0 },
    ],
    relatedSeo: ["billing-app-shop-owners", "bill-format-small-business"],
  },
  {
    slug: "kirana-grocery",
    profession: "Kirana / Grocery Shops",
    heroLine: "Mobile billing, barcode scanning, customer credit book.",
    intro: "A neighbourhood kirana with 500+ SKUs needs barcode-fast billing, monthly credit (udhaar) tracking, and clean GST handling for packaged goods.",
    specifics: [
      "Scan barcode → bill ready (no typing).",
      "Customer credit book replaces the udhaar khata.",
      "Auto-stock deduction with low-stock alerts.",
      "GST-correct: packaged staples often 0% / 5%, branded snacks 12% / 18%.",
    ],
    sampleItems: [
      { name: "Atta 5kg (Branded)", hsn: "1101", qty: 1, rate: 280, gst: 5 },
      { name: "Refined Oil 1L", hsn: "1507", qty: 1, rate: 145, gst: 5 },
      { name: "Branded Tea 250g", hsn: "0902", qty: 1, rate: 175, gst: 5 },
    ],
    relatedSeo: ["billing-app-shop-owners", "bill-format-small-business"],
  },
  {
    slug: "saloons",
    profession: "Salons / Beauty Parlours",
    heroLine: "Bill services, packages and product sales.",
    intro: "Salons mix services (18% GST) with product sales (12% / 18%). The right format separates the two so GSTR-1 stays clean and ITC on products is preserved.",
    specifics: [
      "Tag each line as 'service' or 'product' for correct GST.",
      "Sell membership packages — issue Bill of Supply at start, recognise revenue per visit.",
      "Track per-stylist commissions in description.",
      "Auto-reminder for next appointment in invoice notes.",
    ],
    sampleItems: [
      { name: "Hair Cut + Wash", hsn: "999722", qty: 1, rate: 500, gst: 18 },
      { name: "Hair Spa", hsn: "999722", qty: 1, rate: 1500, gst: 18 },
      { name: "Hair Serum (Retail)", hsn: "3305", qty: 1, rate: 850, gst: 18 },
    ],
    relatedSeo: ["bill-format-small-business", "billing-app-shop-owners"],
  },
  {
    slug: "tailors",
    profession: "Tailors / Boutique Owners",
    heroLine: "Customer measurements, advance, balance — one place.",
    intro: "Tailors take orders weekly, deliver in 7–14 days. A good invoice format records measurements, advance taken, fabric source, and delivery date.",
    specifics: [
      "Add measurements + customer notes in the description block.",
      "Take 50% advance — show advance + balance in summary.",
      "Use serial numbering by month so 'last month's order' is searchable.",
      "Charge stitching service GST 5% (or 12% for designer wear).",
    ],
    sampleItems: [
      { name: "Salwar Suit Stitching (Cotton)", hsn: "999721", qty: 2, rate: 800, gst: 5 },
      { name: "Lining Fabric 1m", hsn: "5208", qty: 1, rate: 180, gst: 5 },
      { name: "Buttons + Hooks (set)", hsn: "9606", qty: 1, rate: 60, gst: 18 },
    ],
    relatedSeo: ["bill-format-small-business", "free-invoice-generator"],
  },
  {
    slug: "agencies",
    profession: "Marketing Agencies",
    heroLine: "Bill retainers, projects and ad-spend pass-through.",
    intro: "Agencies bill in three buckets: monthly retainer, one-off projects, and ad-spend pass-through. The right format keeps each bucket clean for both GST and client reporting.",
    specifics: [
      "Retainer? Set up recurring invoice on the 1st of every month.",
      "Project? Quote first, milestone bill after acceptance.",
      "Ad-spend pass-through? Show vendor cost + agency markup separately.",
      "TDS Section 194J / 194C — show net payable upfront.",
    ],
    sampleItems: [
      { name: "Monthly Retainer — May 2026", hsn: "998313", qty: 1, rate: 75000, gst: 18 },
      { name: "Meta Ads — Pass-through (₹50,000 budget)", hsn: "998365", qty: 1, rate: 50000, gst: 18 },
      { name: "Agency Markup (12%)", hsn: "998313", qty: 1, rate: 6000, gst: 18 },
    ],
    relatedSeo: ["quotation-maker", "freelancer-invoice-tool"],
  },
  {
    slug: "freelance-developers",
    profession: "Freelance Software Developers",
    heroLine: "Hourly, project, retainer — bill it all in 30 seconds.",
    intro: "Indian freelance devs work for both Indian SMBs and foreign clients. The right invoice format handles SAC 998314, multi-currency, and TDS correctly.",
    specifics: [
      "Use SAC 998314 for software development services.",
      "Foreign client? Issue Export Invoice with LUT (0% GST).",
      "Indian client? Tax Invoice with 18% GST.",
      "Indian client deducts 10% TDS — show net payable upfront.",
    ],
    sampleItems: [
      { name: "Web App Sprint (40 hrs @ ₹2,000)", hsn: "998314", qty: 40, rate: 2000, gst: 18 },
      { name: "API Integration", hsn: "998314", qty: 1, rate: 25000, gst: 18 },
      { name: "Bug-fix Hours (10 hrs @ ₹1,800)", hsn: "998314", qty: 10, rate: 1800, gst: 18 },
    ],
    relatedSeo: ["freelancer-invoice-tool", "free-invoice-generator"],
  },
  {
    slug: "ca-tax-professionals",
    profession: "CAs / Tax Professionals",
    heroLine: "Bill clients per filing, retainer or hourly.",
    intro: "CAs run a billing practice on top of their compliance practice. The right format handles per-filing fees, monthly retainers and hourly advisory cleanly.",
    specifics: [
      "Use SAC 998222 for CA services (audit, ITR, GSTR).",
      "Most clients deduct 10% TDS under Section 194J.",
      "Issue separate invoices per service so client books are clean.",
      "InvoiceSetu's CA dashboard manages all clients from one screen.",
    ],
    sampleItems: [
      { name: "GSTR-1 + 3B Filing — May 2026", hsn: "998222", qty: 1, rate: 4500, gst: 18 },
      { name: "ITR Filing — Individual", hsn: "998222", qty: 1, rate: 3000, gst: 18 },
      { name: "Advisory Call (1 hour)", hsn: "998222", qty: 1, rate: 5000, gst: 18 },
    ],
    relatedSeo: ["invoice-software-traders", "quotation-maker"],
  },
  {
    slug: "transporters",
    profession: "Transporters / Logistics",
    heroLine: "Bill freight, demurrage and detention — GST-correct.",
    intro: "Transporters operate under a special GST scheme (5% with no ITC for passenger; reverse charge for goods). The right invoice format handles RCM compliance correctly.",
    specifics: [
      "GTA (Goods Transport Agency) — 5% GST (no ITC) OR 12% GST (with ITC).",
      "Reverse charge applies on most B2B transactions — note this clearly.",
      "Show consignee + consignor + LR number in description.",
      "Itemise freight, demurrage, detention separately.",
    ],
    sampleItems: [
      { name: "Freight Charges — Mumbai to Delhi (LR 12345)", hsn: "9965", qty: 1, rate: 45000, gst: 5 },
      { name: "Demurrage (1 day)", hsn: "9965", qty: 1, rate: 3000, gst: 5 },
      { name: "Detention Charges", hsn: "9965", qty: 1, rate: 2000, gst: 5 },
    ],
    relatedSeo: ["invoice-software-traders", "quotation-maker"],
  },
  {
    slug: "manufacturers",
    profession: "Small Manufacturers",
    heroLine: "Bill goods, with HSN summary and e-invoicing ready.",
    intro: "SME manufacturers issue 100+ invoices/month with multiple HSN codes. The right format auto-generates HSN summary and is e-invoicing-ready for ₹5 Cr+ turnover.",
    specifics: [
      "Multi-HSN summary auto-generated (mandatory above ₹5 Cr).",
      "E-invoicing IRN + QR for ₹5 Cr+ turnover (mandatory).",
      "Bulk dispatch? Use bulk invoice import from Excel.",
      "Track stock movement linked to each invoice.",
    ],
    sampleItems: [
      { name: "Steel Pipes 2 inch (100 nos)", hsn: "7306", qty: 100, rate: 580, gst: 18 },
      { name: "MS Plates 5mm (10 sheets)", hsn: "7208", qty: 10, rate: 4500, gst: 18 },
      { name: "Galvanised Wire 4mm (50 kg)", hsn: "7217", qty: 50, rate: 95, gst: 18 },
    ],
    relatedSeo: ["invoice-software-traders", "quotation-maker"],
  },
  {
    slug: "event-planners",
    profession: "Event Planners",
    heroLine: "Bill venue, catering, decor, talent — all in one quote.",
    intro: "Events stack 8-15 vendors. The right invoice format makes pass-throughs clean, advance + balance crystal clear, and GST treatment vendor-by-vendor correct.",
    specifics: [
      "Quote first → 50% advance on booking → balance 7 days before event.",
      "Pass-through vendors: show their invoice + your coordination markup.",
      "Add cancellation policy in terms (full / 50% / nil based on notice).",
      "Itemise venue, decor, catering, talent, AV separately.",
    ],
    sampleItems: [
      { name: "Venue Booking — Hotel Banquet", hsn: "996311", qty: 1, rate: 175000, gst: 18 },
      { name: "Catering (200 pax @ ₹950)", hsn: "996331", qty: 200, rate: 950, gst: 18 },
      { name: "Decor + Floral", hsn: "998391", qty: 1, rate: 65000, gst: 18 },
    ],
    relatedSeo: ["quotation-maker", "freelancer-invoice-tool"],
  },
  {
    slug: "real-estate-agents",
    profession: "Real Estate Agents / Brokers",
    heroLine: "Bill brokerage, advisory and marketing services.",
    intro: "Real estate agents earn brokerage on closed deals. The right invoice format handles brokerage % calculations, advance retainers and marketing pass-through.",
    specifics: [
      "Brokerage typically 1-2% of deal value — show clearly.",
      "GST 18% on brokerage services.",
      "Buyer side often deducts 5% TDS (Section 194-IB / 194-IA for buyers).",
      "Advisory retainer? Use recurring monthly invoice.",
    ],
    sampleItems: [
      { name: "Brokerage — Apartment Sale (₹85L @ 1%)", hsn: "997221", qty: 1, rate: 85000, gst: 18 },
      { name: "Property Advisory Retainer", hsn: "997221", qty: 1, rate: 15000, gst: 18 },
      { name: "Photoshoot + Listing Markup", hsn: "997221", qty: 1, rate: 8000, gst: 18 },
    ],
    relatedSeo: ["quotation-maker", "freelancer-invoice-tool"],
  },
];
